import { TouristSite, Settings, TourQuestionnaire } from '../types';
import { DistanceMatrix } from './matrixService';

export const getDistance = (
  loc1: { lat: number, lng: number },
  loc2: { lat: number, lng: number },
  id1?: string,
  id2?: string,
  settings?: Settings,
  mergedMatrix?: DistanceMatrix
): number => {
  const matrix = mergedMatrix || settings?.distanceMatrices;
  if (id1 && id2 && matrix) {
    const pairKey = [id1, id2].sort().join('_');
    const entry = (matrix as any)[pairKey];
    if (entry) return entry.distance;
  }
  const R = 6371;
  const dLat = (loc2.lat - loc1.lat) * Math.PI / 180;
  const dLng = (loc2.lng - loc1.lng) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(loc1.lat * Math.PI / 180) * Math.cos(loc2.lat * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c * 1.3;
};

export const getTravelTime = (
  distanceKm: number,
  id1?: string,
  id2?: string,
  settings?: Settings,
  mergedMatrix?: DistanceMatrix
): number => {
  const matrix = mergedMatrix || settings?.distanceMatrices;
  if (id1 && id2 && matrix) {
    const pairKey = [id1, id2].sort().join('_');
    const entry = (matrix as any)[pairKey];
    if (entry) return entry.duration;
  }
  return Math.round((distanceKm / 45) * 60);
};

const addMinutes = (time: string, minutes: number): string => {
  const [h, m] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(h, m, 0, 0);
  date.setMinutes(date.getMinutes() + Math.round(minutes));
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

export const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const getDurationMinutes = (start: string, end: string): number => {
  const startMins = timeToMinutes(start);
  let endMins = timeToMinutes(end);
  if (endMins < startMins) endMins += 1440;
  return endMins - startMins;
};

export interface CircuitStep {
  type: 'travel' | 'activity' | 'meal' | 'pickup' | 'dropoff';
  site?: TouristSite;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  distanceKm?: number;
  detourKm?: number;
  label: string;
  swimLunchOpportunity?: boolean;
}

// ─── Shared optimizeRoute (single implementation) ───────────────────────────
const optimizeRoute = (
  sites: TouristSite[],
  startLoc: { lat: number, lng: number },
  startTime: string,
  startId: string,
  settings: Settings,
  mergedMatrix?: DistanceMatrix
): TouristSite[] => {
  const unvisited = [...sites];
  const optimized: TouristSite[] = [];
  let currentLoc = startLoc;
  let currentId = startId;
  let currentTime = startTime;

  while (unvisited.length > 0) {
    let bestIdx = -1;
    let minScore = Infinity;

    for (let i = 0; i < unvisited.length; i++) {
      const site = unvisited[i];
      const dist = getDistance(currentLoc, site.location, currentId, site.id, settings, mergedMatrix);
      const travelTime = getTravelTime(dist, currentId, site.id, settings, mergedMatrix);
      const arrivalMins = timeToMinutes(addMinutes(currentTime, travelTime));
      let score = travelTime;
      const isBaignade = site.categories?.includes('baignade') ||
        site.tags?.some((t: string) => t.toLowerCase().includes('baignade'));
      if (isBaignade && arrivalMins < 11 * 60) score += 10000;
      if (score < minScore) { minScore = score; bestIdx = i; }
    }

    const nextSite = unvisited[bestIdx];
    optimized.push(nextSite);
    unvisited.splice(bestIdx, 1);
    const dist = getDistance(currentLoc, nextSite.location, currentId, nextSite.id, settings, mergedMatrix);
    const travelTime = getTravelTime(dist, currentId, nextSite.id, settings, mergedMatrix);
    currentTime = addMinutes(addMinutes(currentTime, travelTime), (nextSite.durationMinutes || 60) + (settings.bufferTimeMinutes || 15));
    currentLoc = nextSite.location;
    currentId = nextSite.id;
  }
  return optimized;
};

// ─── Lunch helpers ───────────────────────────────────────────────────────────
const needsLunch = (departureTime: string): boolean =>
  timeToMinutes(departureTime) < timeToMinutes('12:00');

const getLunchWindow = (questionnaire: TourQuestionnaire): { start: string; end: string } => {
  const hasChildrenUnder6 =
    (questionnaire.passengers?.children3_6 || 0) > 0 ||
    (questionnaire.passengers?.babies || 0) > 0;
  return hasChildrenUnder6 ? { start: '11:30', end: '13:00' } : { start: '12:00', end: '14:00' };
};

const getMealDuration = (questionnaire: TourQuestionnaire, settings: Settings, dayNum: number): number => {
  const selectedRestoId = questionnaire.selectedRestaurants?.[dayNum]?.lunch;
  const catering = questionnaire.dailyCatering?.[dayNum] || { lunch: questionnaire.catering };
  const isPicnic = catering.lunch?.type === 'pique-nique' || selectedRestoId === 'picnic';
  if (selectedRestoId === 'picnic') return 60;
  if (isPicnic) return settings.picnicDurationMinutes || 45;
  return settings.lunchDurationMinutes || 90;
};

// ─── EXPORTED: simulate one day duration (used for feasibility + Step9_5) ───
export const simulateDayDuration = (
  sites: TouristSite[],
  startLoc: { lat: number, lng: number },
  endLoc: { lat: number, lng: number },
  startId: string,
  endId: string,
  departureTime: string,
  questionnaire: TourQuestionnaire,
  settings: Settings,
  dayNum: number,
  allSites: TouristSite[] = [],
  mergedMatrix?: DistanceMatrix
): { duration: number; km: number } => {
  const circuit = optimizeRoute(sites, startLoc, departureTime, startId, settings, mergedMatrix);
  let currentTime = departureTime;
  let dayKm = 0;
  let lastLoc = startLoc;
  let lastId = startId;

  const lunchNeeded = needsLunch(departureTime);
  const lunchWindow = getLunchWindow(questionnaire);
  const mealDuration = getMealDuration(questionnaire, settings, dayNum);
  let mealAdded = false;

  const selectedRestoId = questionnaire.selectedRestaurants?.[dayNum]?.lunch;
  const selectedResto = (selectedRestoId && selectedRestoId !== 'picnic')
    ? allSites.find(s => s.id === selectedRestoId) ?? null
    : null;

  for (const site of circuit) {
    const dist = getDistance(lastLoc, site.location, lastId, site.id, settings, mergedMatrix);
    const travelTime = getTravelTime(dist, lastId, site.id, settings, mergedMatrix);
    const arrivalTime = addMinutes(currentTime, travelTime);

    if (lunchNeeded && !mealAdded && timeToMinutes(arrivalTime) >= timeToMinutes(lunchWindow.start)) {
      mealAdded = true;
      if (selectedResto) {
        const dr = getDistance(lastLoc, selectedResto.location, lastId, selectedResto.id, settings, mergedMatrix);
        const tr = getTravelTime(dr, lastId, selectedResto.id, settings, mergedMatrix);
        currentTime = addMinutes(currentTime, tr + mealDuration);
        dayKm += dr;
        lastLoc = selectedResto.location;
        lastId = selectedResto.id;
      } else {
        currentTime = addMinutes(arrivalTime, mealDuration);
      }
    }

    dayKm += dist;
    currentTime = addMinutes(addMinutes(currentTime, travelTime), (site.durationMinutes || 60) + (settings.bufferTimeMinutes || 15));
    lastLoc = site.location;
    lastId = site.id;
  }

  if (lunchNeeded && !mealAdded) {
    const forced = currentTime < lunchWindow.start ? lunchWindow.start : currentTime;
    currentTime = addMinutes(forced, mealDuration);
  }

  const returnDist = getDistance(lastLoc, endLoc, lastId, endId, settings, mergedMatrix);
  currentTime = addMinutes(currentTime, getTravelTime(returnDist, lastId, endId, settings, mergedMatrix));
  dayKm += returnDist;

  return { duration: getDurationMinutes(departureTime, currentTime), km: dayKm };
};

// ─── EXPORTED: calculate ideal number of days needed ────────────────────────
export const calculateIdealDays = (
  questionnaire: TourQuestionnaire,
  selectedSites: TouristSite[],
  settings: Settings,
  driverHomeLocation: { lat: number, lng: number },
  allSites: TouristSite[] = [],
  mergedMatrix?: DistanceMatrix
): number => {
  const pickupLoc = questionnaire.pickupLocation || { lat: 0, lng: 0 };
  const maxDuration = (settings.tourMaxDurationFullDay || 10) * 60 + 30;
  let remaining = [...selectedSites];
  let days = 0;

  while (remaining.length > 0) {
    days++;
    const startLoc = days === 1 ? pickupLoc : (questionnaire.accommodationCoords || pickupLoc);
    const endLoc = pickupLoc;
    const startId = days === 1 ? 'PICKUP' : 'ACCOMMODATION';
    const dayDepartureTime = questionnaire.departureTimes?.[days] || questionnaire.departureTime;

    let fittedToday: TouristSite[] = [];
    let i = 0;
    while (i < remaining.length) {
      const testSites = [...fittedToday, remaining[i]];
      const { duration } = simulateDayDuration(
        testSites, startLoc, endLoc, startId, 'PICKUP',
        dayDepartureTime, questionnaire, settings, days, allSites, mergedMatrix
      );
      if (duration <= maxDuration) {
        fittedToday = testSites;
        remaining.splice(i, 1);
      } else {
        i++;
      }
    }
    if (fittedToday.length === 0 && remaining.length > 0) fittedToday = [remaining.shift()!];
    if (days > 10) break;
  }

  return Math.max(1, days);
};

// ─── MAIN: generate full circuit ────────────────────────────────────────────
export const generateCircuit = (
  questionnaire: TourQuestionnaire,
  selectedSites: TouristSite[],
  settings: Settings,
  driverHomeLocation: { lat: number, lng: number },
  allSites: TouristSite[] = [],
  mergedMatrix?: DistanceMatrix
) => {
  const numberOfDays = questionnaire.logistics?.days || 1;
  const pickupLoc = questionnaire.pickupLocation || { lat: 0, lng: 0 };
  const pickupCity = questionnaire.pickupLocation?.name || 'votre lieu de départ';
  const pickupAddress = questionnaire.pickupLocation?.address || pickupCity;

  let allSteps: CircuitStep[] = [];
  let allCircuit: TouristSite[] = [];
  let totalKm = 0;
  let totalDuration = 0;
  let remainingSites = [...selectedSites];

  for (let day = 0; day < numberOfDays; day++) {
    const dayNum = day + 1;
    let currentDaySites: TouristSite[] = [];
    const startLoc = day === 0 ? pickupLoc : (questionnaire.accommodationCoords || pickupLoc);
    const endLoc = (day === numberOfDays - 1 || questionnaire.accommodationType === 'return')
      ? pickupLoc : (questionnaire.accommodationCoords || pickupLoc);
    const startId = day === 0 ? 'PICKUP' : 'ACCOMMODATION';
    const endId = (day === numberOfDays - 1 || questionnaire.accommodationType === 'return') ? 'PICKUP' : 'ACCOMMODATION';
    const dayDepartureTime = questionnaire.departureTimes?.[dayNum] || questionnaire.departureTime;
    const maxDuration = (settings.tourMaxDurationFullDay || 10) * 60 + 30;

    let i = 0;
    while (i < remainingSites.length) {
      const testSites = [...currentDaySites, remainingSites[i]];
      let kmForConstraint = day === 0 ? 0
        : getDistance(driverHomeLocation, startLoc, 'DRIVER_HOME', startId, settings, mergedMatrix);
      const { duration, km } = simulateDayDuration(
        testSites, startLoc, endLoc, startId, endId,
        dayDepartureTime, questionnaire, settings, dayNum, allSites, mergedMatrix
      );
      kmForConstraint += km;
      if (day > 0) kmForConstraint += getDistance(endLoc, driverHomeLocation, endId, 'DRIVER_HOME', settings, mergedMatrix);

      if (kmForConstraint <= (settings.tourMaxKm || 200) && duration <= maxDuration) {
        currentDaySites = testSites;
        remainingSites.splice(i, 1);
      } else {
        i++;
      }
    }

    const dayResult = runFullDaySimulation(
      currentDaySites, day, numberOfDays, questionnaire, settings,
      driverHomeLocation, pickupLoc, pickupCity, pickupAddress, allSites, mergedMatrix
    );
    allSteps.push(...dayResult.steps);
    allCircuit.push(...dayResult.circuit);
    totalKm += dayResult.dayKm;
    totalDuration += dayResult.dayDuration;
  }

  if (allCircuit.length === 0 && selectedSites.length > 0) {
    return { error: "Aucun circuit ne respecte les contraintes. Veuillez réduire votre sélection ou augmenter le nombre de jours." };
  }

  const price = calculatePrice(allCircuit, questionnaire, settings, allSteps, numberOfDays);
  return { circuit: allCircuit, steps: allSteps, totalKm, price, totalDuration };
};

// ─── Price: per-day with tolerance ──────────────────────────────────────────
const calculatePrice = (
  circuit: TouristSite[],
  questionnaire: TourQuestionnaire,
  settings: Settings,
  allSteps: CircuitStep[],
  numberOfDays: number
): number => {
  const halfDayThreshold = (settings.tourMaxDurationHalfDay || 5) * 60 + 30;
  const dayDurations: number[] = [];
  let dayStart: string | null = null;
  let dayEnd: string | null = null;

  for (const step of allSteps) {
    if (step.type === 'pickup') { dayStart = step.startTime; dayEnd = step.startTime; }
    if (dayStart) dayEnd = step.endTime;
    if (step.type === 'dropoff' && dayStart && dayEnd) {
      dayDurations.push(getDurationMinutes(dayStart, dayEnd));
      dayStart = null; dayEnd = null;
    }
  }

  let basePrice = 0;
  for (let d = 0; d < numberOfDays; d++) {
    const dur = dayDurations[d] || 0;
    basePrice += dur > halfDayThreshold ? (settings.tourPriceFullDay || 0) : (settings.tourPriceHalfDay || 0);
  }

  const adults = (questionnaire.passengers.adults || 0) + (questionnaire.passengers.seniors || 0);
  const c6_12 = questionnaire.passengers.children6_12 || 0;
  const c3_6 = questionnaire.passengers.children3_6 || 0;
  const babies = questionnaire.passengers.babies || 0;

  const activityCost = circuit.reduce((acc, site) => {
    if (site.isFree) return acc;
    return acc + (site.adultPrice || 0) * adults + (site.child6_12Price || 0) * c6_12
      + (site.child3_6Price || 0) * c3_6 + (site.babyPrice || 0) * babies;
  }, 0);

  return Math.round(basePrice + activityCost);
};

// ─── Full day simulation (produces CircuitStep[]) ───────────────────────────
const runFullDaySimulation = (
  daySites: TouristSite[],
  day: number,
  numberOfDays: number,
  questionnaire: TourQuestionnaire,
  settings: Settings,
  driverHome: { lat: number, lng: number },
  pickupLoc: { lat: number, lng: number },
  pickupCity: string,
  pickupAddress: string,
  allSites: TouristSite[],
  mergedMatrix?: DistanceMatrix
) => {
  const dayNum = day + 1;
  const isLastDay = day === numberOfDays - 1;
  const isReturnEveryNight = questionnaire.accommodationType === 'return';
  const startLoc = day === 0 ? pickupLoc : (questionnaire.accommodationCoords || pickupLoc);
  const endLoc = (isLastDay || isReturnEveryNight) ? pickupLoc : (questionnaire.accommodationCoords || pickupLoc);
  const startId = day === 0 ? 'PICKUP' : 'ACCOMMODATION';
  const endId = (isLastDay || isReturnEveryNight) ? 'PICKUP' : 'ACCOMMODATION';
  const dayDepartureTime = questionnaire.departureTimes?.[dayNum] || questionnaire.departureTime;

  const circuit = optimizeRoute(daySites, startLoc, dayDepartureTime, startId, settings, mergedMatrix);
  let currentTime = dayDepartureTime;
  let dayKm = 0;
  let lastLoc = startLoc;
  let lastId = startId;
  const daySteps: CircuitStep[] = [];

  const startSite = day === 0 ? null
    : (questionnaire.selectedAccommodations?.[dayNum]
      ? allSites.find(s => s.id === questionnaire.selectedAccommodations?.[dayNum]) : null);
  daySteps.push({
    type: 'pickup', site: startSite || undefined,
    startTime: currentTime, endTime: currentTime, durationMinutes: 0,
    label: day === 0 ? `Départ de ${pickupAddress}` : `Départ de ${startSite?.name || "l'hébergement"}`
  });

  const lunchNeeded = needsLunch(dayDepartureTime);
  const lunchWindow = getLunchWindow(questionnaire);
  const mealDuration = getMealDuration(questionnaire, settings, dayNum);
  const catering = questionnaire.dailyCatering?.[dayNum] || { lunch: questionnaire.catering, eveningMealIncluded: false };
  const selectedRestoId = questionnaire.selectedRestaurants?.[dayNum]?.lunch;
  const isPicnic = catering.lunch?.type === 'pique-nique' || selectedRestoId === 'picnic';
  const selectedResto = (selectedRestoId && selectedRestoId !== 'picnic')
    ? allSites.find(s => s.id === selectedRestoId) ?? null : null;
  let mealAdded = false;

  for (const site of circuit) {
    const distToSite = getDistance(lastLoc, site.location, lastId, site.id, settings, mergedMatrix);
    const travelToSite = getTravelTime(distToSite, lastId, site.id, settings, mergedMatrix);
    const arrivalAtSite = addMinutes(currentTime, travelToSite);

    if (lunchNeeded && !mealAdded && timeToMinutes(arrivalAtSite) >= timeToMinutes(lunchWindow.start)) {
      mealAdded = true;
      const isBaignade = site.categories?.includes('baignade') ||
        site.tags?.some((t: string) => t.toLowerCase().includes('baignade'));
      const swimLunchOpportunity = isBaignade && isPicnic &&
        timeToMinutes(arrivalAtSite) <= timeToMinutes(lunchWindow.end);

      if (selectedResto) {
        const dr = getDistance(lastLoc, selectedResto.location, lastId, selectedResto.id, settings, mergedMatrix);
        const tr = getTravelTime(dr, lastId, selectedResto.id, settings, mergedMatrix);
        const drToSite = getDistance(selectedResto.location, site.location, selectedResto.id, site.id, settings, mergedMatrix);
        const detourKm = Math.max(0, (dr + drToSite) - distToSite);
        const t0 = currentTime;
        currentTime = addMinutes(currentTime, tr);
        dayKm += dr;
        daySteps.push({ type: 'travel', startTime: t0, endTime: currentTime, durationMinutes: tr, distanceKm: dr, label: '' });
        const ms = currentTime;
        currentTime = addMinutes(currentTime, mealDuration);
        daySteps.push({ type: 'meal', site: selectedResto, startTime: ms, endTime: currentTime, durationMinutes: mealDuration, distanceKm: dr, detourKm, label: `Déjeuner : ${selectedResto.name}` });
        lastLoc = selectedResto.location; lastId = selectedResto.id;
      } else {
        const ms = currentTime;
        currentTime = addMinutes(currentTime, mealDuration);
        daySteps.push({ type: 'meal', startTime: ms, endTime: currentTime, durationMinutes: mealDuration, swimLunchOpportunity, label: swimLunchOpportunity ? "Pique-nique au bord de l'eau 🏊" : isPicnic ? "Pause Pique-nique" : "Déjeuner" });
      }
    }

    const recalcDist = getDistance(lastLoc, site.location, lastId, site.id, settings, mergedMatrix);
    const recalcTravel = getTravelTime(recalcDist, lastId, site.id, settings, mergedMatrix);
    const t0 = currentTime;
    currentTime = addMinutes(currentTime, recalcTravel);
    dayKm += recalcDist;
    daySteps.push({ type: 'travel', startTime: t0, endTime: currentTime, durationMinutes: recalcTravel, distanceKm: recalcDist, label: '' });

    const visitDuration = site.durationMinutes || 60;
    const vs = currentTime;
    currentTime = addMinutes(vs, visitDuration);
    daySteps.push({ type: 'activity', site, startTime: vs, endTime: currentTime, durationMinutes: visitDuration, label: site.name });
    currentTime = addMinutes(currentTime, settings.bufferTimeMinutes || 15);
    lastLoc = site.location; lastId = site.id;
  }

  if (lunchNeeded && !mealAdded) {
    const forced = currentTime < lunchWindow.start ? lunchWindow.start : currentTime;
    const ms = forced;
    currentTime = addMinutes(forced, mealDuration);
    daySteps.push({ type: 'meal', startTime: ms, endTime: currentTime, durationMinutes: mealDuration, label: isPicnic ? "Pause Pique-nique" : "Déjeuner" });
  }

  // Evening meal: only hotel, not last day, not returning every night
  if (!isLastDay && !isReturnEveryNight && questionnaire.accommodationType === 'hotel' && catering.eveningMealIncluded) {
    const eveningRestoId = questionnaire.selectedRestaurants?.[dayNum]?.dinner;
    const isEveningPicnic = eveningRestoId === 'picnic';
    const eveningResto = (eveningRestoId && eveningRestoId !== 'picnic')
      ? allSites.find(s => s.id === eveningRestoId) ?? null : null;
    const eveningDuration = 90;

    if (eveningResto) {
      const dr = getDistance(lastLoc, eveningResto.location, lastId, eveningResto.id, settings, mergedMatrix);
      const tr = getTravelTime(dr, lastId, eveningResto.id, settings, mergedMatrix);
      const t0 = currentTime;
      currentTime = addMinutes(currentTime, tr);
      dayKm += dr;
      daySteps.push({ type: 'travel', startTime: t0, endTime: currentTime, durationMinutes: tr, distanceKm: dr, label: '' });
      const ms = currentTime;
      currentTime = addMinutes(currentTime, eveningDuration);
      daySteps.push({ type: 'meal', site: eveningResto, startTime: ms, endTime: currentTime, durationMinutes: eveningDuration, distanceKm: dr, label: `Dîner : ${eveningResto.name}` });
      lastLoc = eveningResto.location; lastId = eveningResto.id;
    } else {
      const ms = currentTime;
      currentTime = addMinutes(currentTime, eveningDuration);
      daySteps.push({ type: 'meal', startTime: ms, endTime: currentTime, durationMinutes: eveningDuration, label: isEveningPicnic ? "Dîner Pique-nique" : "Dîner" });
    }
  }

  const returnDist = getDistance(lastLoc, endLoc, lastId, endId, settings, mergedMatrix);
  const returnTime = getTravelTime(returnDist, lastId, endId, settings, mergedMatrix);
  const t0 = currentTime;
  currentTime = addMinutes(currentTime, returnTime);
  dayKm += returnDist;
  daySteps.push({ type: 'travel', startTime: t0, endTime: currentTime, durationMinutes: returnTime, distanceKm: returnDist, label: '' });

  const endSite = isLastDay ? null
    : (questionnaire.selectedAccommodations?.[dayNum]
      ? allSites.find(s => s.id === questionnaire.selectedAccommodations?.[dayNum]) : null);
  daySteps.push({
    type: 'dropoff', site: endSite || undefined,
    startTime: currentTime, endTime: currentTime, durationMinutes: 0,
    label: (isLastDay || isReturnEveryNight) ? `Retour à votre lieu de séjour` : `Arrivée à ${endSite?.name || "l'hébergement"}`
  });

  return { steps: daySteps, circuit, dayKm, dayDuration: getDurationMinutes(dayDepartureTime, currentTime) };
};
